




























































































import { computed, defineComponent, ref, toRefs, watch } from '@nuxtjs/composition-api';
import { purify } from '~/directives/purify-html';
import { useCartAttributes, useSpinTheWheel } from '~/features/cart';
import { PromotionOption } from '~/features/cart/utils';
import { useWheelGifts } from '~/features/gift-tiers';
import GiftsWheel from '@/components/SpinTheWheel/Wheel.vue';
import RewardCard from '@/components/SpinTheWheel/RewardCard.vue';
import { useAuth } from '~/features/auth';
import { useAlerts } from '~/features/alerts';
import { useI18n } from '~/features/i18n';
import { useRouter } from '~/features/router';

export default defineComponent({
  name: 'SpinTheWheelModal',
  directives: {
    purify,
  },
  components: {
    GiftsWheel,
    RewardCard,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentTier: {
      type: Number,
      default: 0,
    },
    amountRemaining: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const isOpen = ref(false);
    const isSpun = ref(false);
    const isMinimumSpinningPassed = ref(true);
    const giftWonIndex = ref(0);
    const finalRotationPosition = ref(0);
    const { value } = toRefs(props);
    const wheelColors = ['#F2EAE1', '#D0DDD4', '#E0D2D5'];
    const { user } = useAuth();
    const { warn } = useAlerts();
    const { t } = useI18n();
    const { redirect } = useRouter();
    const { spinTheWheel, isSpinning } = useSpinTheWheel();
    const showAfterSpinning = computed(() => isSpun.value && isMinimumSpinningPassed.value);

    watch(value, value => {
      isOpen.value = value;
    });

    const { cartPromotions, spinTheWheelData } = useCartAttributes();
    const { wheelGifts } = useWheelGifts(computed(() => cartPromotions.value.options as PromotionOption[]));

    /**
     * Computes a shuffled list of gifts, alternating between two tiers.
     *
     * This function creates a new list of gifts by interleaving gifts from two tiers
     * (first and second). Gifts are selected from the `wheelGifts` array, filtered by `minimumTier`.
     * If one tier has extra gifts, they are appended at the end.
     *
     * @returns {Array<Object>} An array of gift objects, alternating between first- and second-tier gifts.
     *
     * Example:
     *  Given wheelGifts = [
     *    { id: 1, minimumTier: 1, name: 'Gift A' },
     *    { id: 2, minimumTier: 2, name: 'Gift B' },
     *    ...
     *  ]
     *  The returned array will alternate between minimumTier 1 and 2 gifts.
     */
    const shuffledGifts = computed(() => {
      const firstTierGifts = wheelGifts.value.filter(gift => gift.minimumTier === 1);
      const secondTierGifts = wheelGifts.value.filter(gift => gift.minimumTier === 2);
      const gifts = [];
      let i = 0;
      for (; i < firstTierGifts.length && i < secondTierGifts.length; i++) {
        gifts.push(firstTierGifts[i]);
        gifts.push(secondTierGifts[i]);
      }

      if (i < firstTierGifts.length) {
        gifts.push(...firstTierGifts.slice(i));
      }

      if (i < secondTierGifts.length) {
        gifts.push(...secondTierGifts.slice(i));
      }

      return gifts;
    });

    async function doSpinTheWheel() {
      isMinimumSpinningPassed.value = false;
      if (!user?.value) {
        warn(t('loginFirst').toString());
        redirect('/login');
        return;
      }

      // Set minimum spinning time to 3 seconds
      setTimeout(() => {
        isMinimumSpinningPassed.value = true;
      }, 3000);

      const { data } = await spinTheWheel();

      if (data) {
        // Match sku of gift won with sku of gifts in wheelGifts
        giftWonIndex.value = shuffledGifts.value.findIndex(
          gift => gift.sku === data.response?.cart.spinTheWheelData?.product_won?.sku
        );
        finalRotationPosition.value = 360 - 45 * giftWonIndex.value;

        // Don't show unless spinning animation is done (transition to final rotation is set to 2 seconds)
        setTimeout(() => {
          isSpun.value = true;
        }, 2000);
      }
    }

    return {
      isOpen,
      giftWonIndex,
      isSpinning,
      finalRotationPosition,
      shuffledGifts,
      wheelColors,
      doSpinTheWheel,
      spinTheWheelData,
      showAfterSpinning,
      isMinimumSpinningPassed,
    };
  },
});
